/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: WebSocket Implementation
 * PURPOSE: Real-time permission synchronization with cache-first strategy
 *
 * NEW PATTERNS IMPLEMENTED:
 * - Service Pattern: Singleton service with lifecycle management
 * - Cache-First Strategy: Load from localStorage cache, validate in background
 * - Event-Driven Updates: WebSocket messages trigger permission refresh
 *
 * KEY FEATURES:
 * - 24-hour permission cache with version management
 * - Background validation after 2-second delay
 * - WebSocket listener for real-time permission updates
 * - Memory leak prevention with cleanup on page unload
 *
 * RELATED FILES:
 * - src/lib/state/auth.state.svelte.ts
 * - src/lib/services/api/profile.api.ts
 */

// src/lib/services/websocket/permission-sync.ts
import { browser } from '$app/environment';
import {
	authState,
	setFreshPermissions,
	updateUserProfile,
	clearAuthentication,
	redirectToLogin
} from '$lib/state/auth.state.svelte';
import { eventBus } from '$lib/core/services/EventBusService';
import { profileApi } from '$lib/services/api/profile.api';
import { createLogger } from '$lib/utils/logger';
import type { UserPermissions } from '$lib/types/core/user.types';

const logger = createLogger('websocket.permission-sync');

const PERMISSION_CACHE_KEY = 'user_permissions';
const PERMISSION_CACHE_VERSION = '1.0';
const PERMISSION_CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours

export class PermissionSyncService {
	private static instance: PermissionSyncService;
	private initialized = false;
	private currentUserId: string | null = null;
	private beforeUnloadHandler: (() => void) | null = null;
	// Drain-loop single-flight: collapse bursts of PERMISSION_UPDATE into the
	// minimum number of refreshes while never dropping the last one.
	private refreshInFlight = false;
	private refreshPending = false;

	private constructor() {}

	static getInstance(): PermissionSyncService {
		if (!PermissionSyncService.instance) {
			PermissionSyncService.instance = new PermissionSyncService();
		}
		return PermissionSyncService.instance;
	}

	/**
	 * Initialize the permission sync service
	 */
	async initialize(userId: string): Promise<void> {
		if (this.initialized && this.currentUserId === userId) {
			logger.debug('Permission sync service already initialized', { userId });
			return;
		}

		this.currentUserId = userId;

		// Load permissions from cache first (instant UI)
		this.loadFromCache();

		// Live permission updates are now delivered via the WebSocket MessageRouter
		// (MessageType.PERMISSION_UPDATE -> refreshPermissions). No event-bus listener needed.

		// Validate permissions in background
		this.validatePermissionsInBackground();

		// Register cleanup on page unload to prevent memory leaks
		this.beforeUnloadHandler = () => this.cleanup();
		if (browser) {
			window.addEventListener('beforeunload', this.beforeUnloadHandler);
		}

		this.initialized = true;
		logger.info('Permission sync service initialized successfully', {
			userId,
			features: ['cache', 'websocket', 'background_validation', 'cleanup_handler']
		});
	}

	/**
	 * Load permissions from localStorage cache
	 */
	private loadFromCache(): void {
		if (!browser) return;

		try {
			const cacheKey = `${PERMISSION_CACHE_KEY}_${this.currentUserId}`;
			const cached = localStorage.getItem(cacheKey);

			if (cached) {
				const { data, version, timestamp } = JSON.parse(cached);

				// Check cache version and TTL
				const now = Date.now();
				const age = now - timestamp;

				if (version === PERMISSION_CACHE_VERSION && age < PERMISSION_CACHE_TTL) {
					// Update authState with cached permissions
					setFreshPermissions({ direct_permissions: data.direct_permissions || [] });
					logger.debug('Loaded permissions from cache', {
						userId: this.currentUserId,
						cacheAgeMinutes: Math.round(age / 1000 / 60),
						permissionCount: data.direct_permissions?.length || 0,
						version
					});
				} else {
					// Cache expired or version mismatch
					localStorage.removeItem(cacheKey);
					logger.debug('Permission cache expired or outdated', {
						userId: this.currentUserId,
						version,
						expectedVersion: PERMISSION_CACHE_VERSION,
						ageMinutes: Math.round(age / 1000 / 60),
						ttlMinutes: PERMISSION_CACHE_TTL / 1000 / 60
					});
				}
			}
		} catch (error) {
			logger.error('Failed to load permissions from cache', {
				userId: this.currentUserId,
				error: error instanceof Error ? error.message : 'Unknown error'
			});
		}
	}

	/**
	 * Save permissions to localStorage cache
	 */
	private saveToCache(permissions: UserPermissions): void {
		if (!browser || !this.currentUserId) return;

		try {
			const cacheKey = `${PERMISSION_CACHE_KEY}_${this.currentUserId}`;
			const cacheData = {
				data: permissions,
				version: PERMISSION_CACHE_VERSION,
				timestamp: Date.now()
			};

			localStorage.setItem(cacheKey, JSON.stringify(cacheData));
			logger.debug('Saved permissions to cache', {
				userId: this.currentUserId,
				permissionCount: permissions.direct_permissions?.length || 0,
				version: PERMISSION_CACHE_VERSION
			});
		} catch (error) {
			logger.error('Failed to save permissions to cache', {
				userId: this.currentUserId,
				error: error instanceof Error ? error.message : 'Unknown error'
			});
		}
	}

	/**
	 * Refresh permissions from backend
	 */
	async refreshPermissions(): Promise<void> {
		// Drain-loop single-flight: if a refresh is running, mark pending and return; the
		// in-flight loop runs exactly one more pass, so the final fetch always observes state
		// AFTER the last triggering PERMISSION_UPDATE. try/finally guards against a thrown
		// doRefresh wedging inFlight=true forever.
		if (this.refreshInFlight) {
			this.refreshPending = true;
			return;
		}
		this.refreshInFlight = true;
		try {
			do {
				this.refreshPending = false;
				await this.doRefresh();
			} while (this.refreshPending);
		} finally {
			this.refreshInFlight = false;
		}
	}

	/**
	 * Single refresh pass: fetch profile, update permissions + user profile fields live,
	 * or force logout if the account was deactivated/deleted.
	 */
	private async doRefresh(): Promise<void> {
		const startTime = Date.now();
		try {
			const profile = await profileApi.getCurrentProfile();

			// Deactivated/deleted account -> force logout (do NOT keep cached perms).
			if (profile && (profile as any).is_active === false) {
				await this.forceLogout('account_inactive');
				return;
			}

			const permissions = this.extractPermissionsFromProfile(profile);
			setFreshPermissions({ direct_permissions: permissions.direct_permissions || [] });

			// Also refresh role/department/level fields so role-CODE checks (e.g. field-tech)
			// reflect a role removal live, not just the flat permission list.
			updateUserProfile({
				roles: (profile as any).roles ?? [],
				departments: (profile as any).departments ?? [],
				user_level: (profile as any).user_level,
				is_company_user: (profile as any).is_company_user,
				primary_department_id: (profile as any).primary_department_id ?? null
			});

			this.saveToCache(permissions);
			eventBus.emit('permissions:updated', { reason: 'live_refresh' });

			logger.info('Permissions refreshed live', {
				userId: this.currentUserId,
				permissionCount: permissions.direct_permissions?.length || 0,
				duration: Date.now() - startTime
			});
		} catch (error) {
			if (this.isAuthInvalid(error)) {
				await this.forceLogout('auth_invalid');
				return;
			}
			logger.error('Failed to refresh permissions from backend', {
				userId: this.currentUserId,
				error: error instanceof Error ? error.message : 'Unknown error',
				duration: Date.now() - startTime
			});
			// Transient/non-auth error: keep using cached/current permissions.
		}
	}

	/** Distinguish a deactivated/deleted/invalid session from a transient failure. */
	private isAuthInvalid(error: unknown): boolean {
		const e = error as any;
		const status = e?.status ?? e?.response?.status;
		if (status === 401 || status === 403) return true;
		const msg = String(e?.message ?? '').toLowerCase();
		return (
			msg.includes('session expired') ||
			msg.includes('token refresh failed') ||
			msg.includes('unauthorized') ||
			msg.includes('forbidden')
		);
	}

	/** Force the user out live (account deactivated/deleted or session invalid). */
	private async forceLogout(reason: string): Promise<void> {
		logger.warn('Account no longer valid during permission refresh — logging out', {
			userId: this.currentUserId,
			reason
		});
		try {
			clearAuthentication();
			redirectToLogin();
		} catch (e) {
			logger.error('Failed to force logout after auth-invalid refresh', {
				error: e instanceof Error ? e.message : 'Unknown error'
			});
		}
	}

	/**
	 * Validate cached permissions against backend in background
	 */
	private async validatePermissionsInBackground(): Promise<void> {
		// Don't validate immediately, give time for cache to be used
		setTimeout(async () => {
			const startTime = Date.now();
			try {
				logger.debug('Starting background permission validation', {
					userId: this.currentUserId,
					delayMs: 2000
				});

				const profile = await profileApi.getCurrentProfile();
				const freshPermissions = this.extractPermissionsFromProfile(profile);

				// Get current permissions from authState
				const currentPermissions = authState.permissions;

				// Compare and update if different
				if (JSON.stringify(currentPermissions) !== JSON.stringify(freshPermissions)) {
					logger.info('Permissions changed during validation', {
						userId: this.currentUserId,
						oldCount: currentPermissions?.direct_permissions?.length || 0,
						newCount: freshPermissions.direct_permissions?.length || 0,
						duration: Date.now() - startTime
					});

					setFreshPermissions({ direct_permissions: freshPermissions.direct_permissions || [] });
					this.saveToCache(freshPermissions);

					// Emit event
					eventBus.emit('permissions:validated', {
						hadChanges: true
					});
				} else {
					logger.debug('Cached permissions validated successfully', {
						userId: this.currentUserId,
						permissionCount: freshPermissions.direct_permissions?.length || 0,
						duration: Date.now() - startTime
					});
				}
			} catch (error) {
				logger.error('Failed to validate permissions in background', {
					userId: this.currentUserId,
					error: error instanceof Error ? error.message : 'Unknown error',
					duration: Date.now() - startTime
				});
			}
		}, 2000); // 2 second delay
	}

	/**
	 * Clear permission cache for current user
	 */
	clearCache(): void {
		if (!browser || !this.currentUserId) return;

		const cacheKey = `${PERMISSION_CACHE_KEY}_${this.currentUserId}`;
		localStorage.removeItem(cacheKey);
		logger.debug('Permission cache cleared', {
			userId: this.currentUserId
		});
	}

	/**
	 * Extract permissions from user profile
	 */
	private extractPermissionsFromProfile(profile: any): UserPermissions {
		logger.debug('Extracting permissions from profile', {
			userId: this.currentUserId,
			hasProfile: !!profile,
			hasRoles: !!profile?.roles,
			roleCount: profile?.roles?.length
		});

		const permissions: UserPermissions = {
			dashboards: {},
			features: {},
			direct_permissions: [],
			inherited_permissions: [],
			metadata: {}
		};

		if (profile.roles && profile.roles.length > 0) {
			profile.roles.forEach((role: any) => {
				logger.debug('Processing role permissions', {
					userId: this.currentUserId,
					roleName: role.name,
					roleCode: role.code,
					hasPermissions: !!role.permissions,
					resourceCount: Object.keys(role.permissions || {}).length
				});

				if (role.permissions) {
					Object.entries(role.permissions).forEach(([resource, actions]) => {
						// Add to direct_permissions in the correct format
						permissions.direct_permissions.push({
							resource,
							actions: Array.isArray(actions) ? actions : [actions],
							scope: role.code
						});
					});
				}
			});
		}

		logger.debug('Permissions extracted from profile', {
			userId: this.currentUserId,
			directPermissionCount: permissions.direct_permissions.length,
			hasComponentPermissions: permissions.direct_permissions.some(
				(p) => p.resource === 'COMPONENT'
			),
			resources: [...new Set(permissions.direct_permissions.map((p) => p.resource))]
		});

		return permissions;
	}

	/**
	 * Cleanup when user logs out
	 */
	cleanup(): void {
		const userId = this.currentUserId;

		this.clearCache();
		this.currentUserId = null;
		this.initialized = false;

		// Remove page unload listener to prevent memory leaks
		if (this.beforeUnloadHandler && browser) {
			window.removeEventListener('beforeunload', this.beforeUnloadHandler);
			this.beforeUnloadHandler = null;
		}

		logger.info('Permission sync service cleaned up', {
			userId,
			cleanedResources: ['cache', 'websocket_listener', 'unload_handler']
		});
	}
}

// Export singleton instance
export const permissionSyncService = PermissionSyncService.getInstance();
